// src/components/subscription/PricingPlans.jsx
import React, { useState, useEffect } from 'react';
import {
  Check,
  Crown,
  Users,
  GraduationCap,
  Brain,
  Calculator,
  ArrowRight,
  Sparkles,
  Clock,
  Star,
  AlertCircle,
  Zap
} from 'lucide-react';
import { SubscriptionService } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import CheckoutForm from './CheckoutForm';

const PricingPlans = ({ onPlanSelect, selectedPlan, showCheckout = false }) => {
  const { userProfile } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(showCheckout);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const availablePlans = await SubscriptionService.getPlans();
      setPlans(availablePlans);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlanData(plan);
    if (onPlanSelect) {
      onPlanSelect(plan);
    } else {
      setShowCheckoutForm(true);
    }
  };

  const getEarlyBirdCountdown = () => {
    const earlyBirdEnd = new Date('2025-06-30');
    const now = new Date();
    const timeDiff = earlyBirdEnd - now;
    
    if (timeDiff <= 0) return null;
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours };
  };

  const countdown = getEarlyBirdCountdown();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e55a3]"></div>
      </div>
    );
  }

  if (showCheckoutForm && selectedPlanData) {
    return (
      <CheckoutForm 
        plan={selectedPlanData}
        onBack={() => setShowCheckoutForm(false)}
        onSuccess={() => {
          setShowCheckoutForm(false);
          // Handle success (redirect to dashboard, show success message, etc.)
        }}
      />
    );
  }

  const mainPlans = plans.filter(plan => plan.type !== 'addon');
  const addonPlans = plans.filter(plan => plan.type === 'addon');

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Choose Your Perfect
          <span className="block text-[#6e55a3]">Assessment Plan</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
          Streamline your assessment workflow with intelligent analytics and AI-powered insights. 
          Start your journey to better educational outcomes today.
        </p>

        {/* Early Bird Alert */}
        {countdown && (
          <div className="bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 text-orange-800 mb-2">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">Early Bird Special!</span>
            </div>
            <p className="text-sm text-orange-700 mb-2">
              50% off annual plans ends in:
            </p>
            <div className="text-lg font-bold text-orange-900">
              {countdown.days} days, {countdown.hours} hours
            </div>
          </div>
        )}
      </div>

      {/* Main Plans */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8">
        {mainPlans.map((plan) => {
          const isPopular = plan.popular;
          const isSchoolPlan = plan.type === 'school';
          
          return (
            <div 
              key={plan.id}
              className={`relative bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-xl ${
                isPopular 
                  ? 'border-[#6e55a3] shadow-lg scale-105' 
                  : 'border-gray-200 hover:border-[#6e55a3]'
              }`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#6e55a3] text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <Crown className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Early Bird Badge */}
              {plan.discountedPrice && (
                <div className="absolute -top-2 -right-2">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 rounded-full">
                    <Sparkles className="w-4 h-4" />
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className={`inline-flex p-3 rounded-full mb-4 ${
                    isSchoolPlan ? 'bg-blue-100 text-blue-600' : 'bg-[#a9a6c4]/20 text-[#6e55a3]'
                  }`}>
                    {isSchoolPlan ? <Users className="w-8 h-8" /> : <GraduationCap className="w-8 h-8" />}
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 text-sm">{plan.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-6">
                  {plan.discountedPrice ? (
                    <div>
                      <div className="text-sm text-gray-500 line-through">R{plan.originalPrice}</div>
                      <div className="text-3xl font-bold text-[#6e55a3]">
                        R{plan.discountedPrice}
                        {plan.priceUnit && <span className="text-sm text-gray-600">/{plan.priceUnit}</span>}
                      </div>
                      <div className="text-sm text-green-600 font-medium">
                        Save R{plan.savings}!
                      </div>
                    </div>
                  ) : (
                    <div className="text-3xl font-bold text-gray-900">
                      R{plan.price}
                      {plan.priceUnit && <span className="text-sm text-gray-600">/{plan.priceUnit}</span>}
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600 mt-1">
                    per {plan.duration}
                    {plan.duration === 'annual' && !plan.discountedPrice && (
                      <span className="text-green-600 font-medium ml-1">
                        (Save R{plan.savings})
                      </span>
                    )}
                  </div>

                  {plan.minUsers && (
                    <div className="text-xs text-gray-500 mt-1">
                      Minimum {plan.minUsers} educators
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    isPopular
                      ? 'bg-[#6e55a3] text-white hover:bg-[#6156a6] shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  { 'Choose Plan' }
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Reports Add-on */}
      {addonPlans.length > 0 && (
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Supercharge with AI
            </h2>
            <p className="text-gray-600">
              Unlock intelligent performance insights with our AI-powered reporting add-on
            </p>
          </div>

          {addonPlans.map((addon) => (
            <div key={addon.id} className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-[#6e55a3] to-[#887fac] rounded-2xl p-8 text-white">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                  {/* Icon & Title */}
                  <div className="text-center lg:text-left">
                    <div className="inline-flex p-4 bg-white/20 rounded-full mb-4">
                      <Brain className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{addon.name}</h3>
                    <p className="text-white/90">{addon.description}</p>
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    {addon.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-300" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing & CTA */}
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">
                      R{addon.price}
                      <span className="text-lg">/learner</span>
                    </div>
                    <div className="text-white/80 text-sm mb-4">per year</div>
                    
                    <button
                      onClick={() => handlePlanSelect(addon)}
                      className="bg-white text-[#6e55a3] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 mx-auto"
                    >
                      Add AI Reports
                      <Brain className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAQ/Info Section */}
      <div className="bg-gray-50 rounded-2xl p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#6e55a3]" />
                Early Bird Special
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
                <p>• 50% off all annual subscriptions</p>
                <p>• Valid until 30 June 2025</p>
                <p>• Automatic renewal at regular price</p>
                <p>• 7-day access for manual payments</p>
            </div>
            </div>

            <div>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#6e55a3]" />
                Payment Options
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
                <p>• Instant PayFast payment processing</p>
                <p>• Manual bank transfer option</p>
                <p>• Secure payment handling</p>
                <p>• Email support included</p>
            </div>
            </div>
        </div>
        </div>
    </div>
  );
};

export default PricingPlans;